var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Bound } from '@blocksuite/blocks';
import { DisposableGroup, noop } from '@blocksuite/global/utils';
import { WithDisposable } from '@blocksuite/lit';
import {} from '@blocksuite/store';
import { css, html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { TOCNoteCard, } from './toc-card.js';
import { startDragging } from './utils/drag.js';
noop(TOCNoteCard);
export class TOCPanelBody extends WithDisposable(LitElement) {
    constructor() {
        super(...arguments);
        this._dragging = false;
        this._notes = [];
        this._hiddenNotes = [];
        this.mode = 'page';
        this.hidePreviewIcon = false;
        /**
         * store the id of selected notes
         */
        this._selected = [];
        this._pageDisposables = null;
        this._indicatorTranslateY = 0;
        this._changedFlag = false;
        this._highlightMask = null;
        this._highlightTimeoutId = null;
    }
    get viewportPadding() {
        return this.fitPadding
            ? [0, 0, 0, 0].map((val, idx) => Number.isFinite(this.fitPadding[idx]) ? this.fitPadding[idx] : val)
            : [0, 0, 0, 0];
    }
    _isEdgelessMode() {
        return this.mode === 'edgeless';
    }
    connectedCallback() {
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (!this._changedFlag && this._oldViewport) {
            const edgeless = this.edgeless;
            if (!edgeless)
                return;
            edgeless.surface.viewport.setViewport(this._oldViewport.zoom, [this._oldViewport.center.x, this._oldViewport.center.y], true);
        }
        this._clearPageDisposables();
        this._highlightMask?.remove();
        this._highlightMask = null;
        if (this._highlightTimeoutId) {
            clearTimeout(this._highlightTimeoutId);
            this._highlightTimeoutId = null;
        }
    }
    _clearPageDisposables() {
        this._pageDisposables?.dispose();
        this._pageDisposables = null;
    }
    _setPageDisposables() {
        const { slots, root } = this.page;
        const slotsForUpdate = root
            ? [root.childrenUpdated, slots.blockUpdated]
            : [slots.blockUpdated];
        slots.rootAdded.on(root => {
            this._clearPageDisposables();
            this._pageDisposables = new DisposableGroup();
            this._pageDisposables.add(root.childrenUpdated.on(() => this._updateNotes()));
        });
        slotsForUpdate.forEach(slot => {
            this._clearPageDisposables();
            this._pageDisposables = new DisposableGroup();
            this._pageDisposables.add(slot.on(() => this._updateNotes()));
        });
        this._updateNotes();
    }
    updated(_changedProperties) {
        if (_changedProperties.has('page') || _changedProperties.has('edgeless')) {
            this._setPageDisposables();
        }
        if (_changedProperties.has('mode') &&
            this.edgeless &&
            this._isEdgelessMode()) {
            if (_changedProperties.get('mode') === undefined)
                return;
            requestAnimationFrame(() => this._zoomToFit());
        }
    }
    _updateNotes() {
        const root = this.page.root;
        if (this._dragging)
            return;
        if (!root) {
            this._notes = [];
            return;
        }
        const visibleNotes = [];
        const hiddenNotes = [];
        const oldSelectedSet = this._selected.reduce((pre, id) => {
            pre.add(id);
            return pre;
        }, new Set());
        const newSelected = [];
        root.children.forEach((block, index) => {
            if (!['affine:note'].includes(block.flavour))
                return;
            const blockModel = block;
            const tocNoteItem = {
                note: block,
                index,
                number: index + 1,
            };
            if (blockModel.hidden) {
                hiddenNotes.push(tocNoteItem);
            }
            else {
                visibleNotes.push(tocNoteItem);
                if (oldSelectedSet.has(block.id)) {
                    newSelected.push(block.id);
                }
            }
        });
        this._notes = visibleNotes;
        this._hiddenNotes = hiddenNotes;
        this._selected = newSelected;
    }
    _moveNotes(index, selected, notesMap, notes, children) {
        if (!this._isEdgelessMode() || !children.length || !this.page.root)
            return;
        const blocks = selected.map(id => notesMap.get(id).note);
        const draggingBlocks = new Set(blocks);
        const targetIndex = index === notes.length ? notes[index - 1].index + 1 : notes[index].index;
        const leftPart = children
            .slice(0, targetIndex)
            .filter(block => !draggingBlocks.has(block));
        const rightPart = children
            .slice(targetIndex)
            .filter(block => !draggingBlocks.has(block));
        const newChildren = [...leftPart, ...blocks, ...rightPart];
        this._changedFlag = true;
        this.page.updateBlock(this.page.root, {
            children: newChildren,
        });
        this.page.root.childrenUpdated.emit();
    }
    _selectNote(e) {
        if (!this._isEdgelessMode())
            return;
        const { selected, id, multiselect } = e.detail;
        if (!selected) {
            this._selected = this._selected.filter(noteId => noteId !== id);
        }
        else if (multiselect) {
            this._selected = [...this._selected, id];
        }
        else {
            this._selected = [id];
        }
        this.edgeless?.selectionManager.setSelection({
            elements: this._selected,
            editing: false,
        });
    }
    _drag() {
        if (!this._selected.length || !this.page.root)
            return;
        this._dragging = true;
        // cache the notes in case it is changed by other peers
        const children = this.page.root.children.slice();
        const notes = this._notes;
        const notesMap = this._notes.reduce((map, note, index) => {
            map.set(note.note.id, {
                ...note,
                number: index + 1,
            });
            return map;
        }, new Map());
        const selected = this._selected.slice();
        startDragging({
            container: this,
            doc: this.ownerDocument,
            host: this.domHost ?? this.ownerDocument,
            page: this.page,
            tocListContainer: this.panelListElement,
            onDragEnd: insertIdx => {
                this._dragging = false;
                this.insertIndex = undefined;
                if (insertIdx === undefined)
                    return;
                this._moveNotes(insertIdx, selected, notesMap, notes, children);
            },
            onDragMove: (idx, indicatorTranslateY) => {
                this.insertIndex = idx;
                this._indicatorTranslateY = indicatorTranslateY ?? 0;
            },
        });
    }
    /*
     * click at blank area to clear selection
     */
    _clickBlank(e) {
        e.stopPropagation();
        // check if click at toc-card, if not, set this._selected to empty
        if (e.target.closest('toc-note-card') ||
            this._selected.length === 0) {
            return;
        }
        this._selected = [];
        this.edgeless?.selectionManager.setSelection({
            elements: this._selected,
            editing: false,
        });
    }
    firstUpdated() {
        this._disposables.addFromEvent(this, 'click', this._clickBlank);
    }
    _zoomToFit() {
        const edgeless = this.edgeless;
        if (!edgeless)
            return;
        const { surface } = edgeless;
        const bound = edgeless.getElementsBound();
        if (!bound)
            return;
        this._oldViewport = {
            zoom: surface.viewport.zoom,
            center: {
                x: surface.viewport.center.x,
                y: surface.viewport.center.y,
            },
        };
        surface.viewport.setViewportByBound(new Bound(bound.x, bound.y, bound.w, bound.h), this.viewportPadding, true);
    }
    _fitToElement(e) {
        const edgeless = this.edgeless;
        if (!edgeless)
            return;
        const { block } = e.detail;
        const bound = Bound.deserialize(block.xywh);
        edgeless.surface.viewport.setViewportByBound(bound, this.viewportPadding, true);
    }
    _scrollToBlock(e) {
        if (this._isEdgelessMode() || !this.editorHost)
            return;
        const pageBlock = this.editorHost.querySelector('affine-doc-page');
        if (!pageBlock)
            return;
        const { blockPath } = e.detail;
        const path = [pageBlock.model.id, ...blockPath];
        const blockElement = this.editorHost.view.viewFromPath('block', path);
        if (!blockElement)
            return;
        blockElement.scrollIntoView({
            behavior: 'instant',
            block: 'center',
            inline: 'center',
        });
        requestAnimationFrame(() => {
            const blockRect = blockElement.getBoundingClientRect();
            const { top, left, width, height } = blockRect;
            const { top: offsetY, left: offsetX } = pageBlock.viewport;
            if (!this._highlightMask) {
                this._highlightMask = document.createElement('div');
                pageBlock.appendChild(this._highlightMask);
            }
            Object.assign(this._highlightMask.style, {
                position: 'absolute',
                top: `${top - offsetY}px`,
                left: `${left - offsetX}px`,
                width: `${width}px`,
                height: `${height}px`,
                background: 'var(--affine-hover-color)',
                borderRadius: '4px',
                display: 'block',
            });
            // Clear the previous timeout if it exists
            if (this._highlightTimeoutId !== null) {
                clearTimeout(this._highlightTimeoutId);
            }
            this._highlightTimeoutId = setTimeout(() => {
                if (this._highlightMask) {
                    this._highlightMask.style.display = 'none';
                }
            }, 1000);
        });
    }
    _renderPanelList() {
        const selectedNotesSet = new Set(this._selected);
        return html `<div class="panel-list">
      ${this.insertIndex !== undefined
            ? html `<div
            class="insert-indicator"
            style=${`transform: translateY(${this._indicatorTranslateY}px)`}
          ></div>`
            : nothing}
      ${this._notes.length
            ? repeat(this._notes, note => note.note.id, (note, idx) => html `
              <toc-note-card
                data-note-id=${note.note.id}
                .note=${note.note}
                .number=${idx + 1}
                .index=${note.index}
                .page=${this.page}
                .editorMode=${this.mode}
                .status=${selectedNotesSet.has(note.note.id)
                ? this._dragging
                    ? 'placeholder'
                    : 'selected'
                : undefined}
                .showCardNumber=${this._notes.length > 1}
                .hidePreviewIcon=${this.hidePreviewIcon}
                @select=${this._selectNote}
                @drag=${this._drag}
                @fitview=${this._fitToElement}
                @clickblock=${this._scrollToBlock}
              ></toc-note-card>
            `)
            : html `${nothing}`}
      ${this._hiddenNotes.length > 0
            ? html `<div class="title">Hidden Contents</div>`
            : nothing}
      ${this._hiddenNotes.length
            ? repeat(this._hiddenNotes, note => note.note.id, (note, idx) => html `<toc-note-card
                  data-note-id=${note.note.id}
                  .note=${note.note}
                  .number=${idx + 1}
                  .index=${note.index}
                  .page=${this.page}
                  .invisible=${true}
                  .showCardNumber=${false}
                  .hidePreviewIcon=${this.hidePreviewIcon}
                  @fitview=${this._fitToElement}
                ></toc-note-card>
                ${idx !== this._hiddenNotes.length - 1
                ? html `<div class="hidden-note-divider"><div></div></div>`
                : nothing} `)
            : nothing}
    </div>`;
    }
    _renderEmptyPanel() {
        return html `<div class="no-note-container">
      <div class="note-placeholder">
        Use headings to create a table of contents.
      </div>
    </div>`;
    }
    render() {
        return html `
      <div class="toc-panel-body-container">
        ${this._notes.length
            ? this._renderPanelList()
            : this._renderEmptyPanel()}
      </div>
    `;
    }
}
TOCPanelBody.styles = css `
    .toc-panel-body-container {
      position: relative;
      display: flex;
      align-items: start;
      box-sizing: border-box;
      flex-direction: column;
      width: 100%;
      position: relative;
      padding: 0 8px;
    }

    .panel-list {
      position: relative;
      width: 100%;
    }

    .panel-list .title {
      width: 100%;
      font-size: 14px;
      line-height: 24px;
      font-weight: 500;
      color: var(--affine-text-secondary-color);
      padding-left: 8px;
      height: 40px;
      box-sizing: border-box;
      padding: 6px 8px;
    }

    .insert-indicator {
      height: 2px;
      border-radius: 1px;
      background-color: var(--affine-blue-500);
      position: absolute;
      contain: layout size;
      width: 100%;
    }

    .no-note-container {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .note-placeholder {
      margin-top: 240px;
      align-self: center;
      width: 190px;
      height: 48px;
      color: var(--affine-text-secondary-color, #8e8d91);
      text-align: center;
      /* light/base */
      font-size: 15px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
    }

    .hidden-note-divider {
      width: 100%;
      padding: 10px 8px;
      box-sizing: border-box;
    }

    .hidden-note-divider div {
      height: 0.5px;
      background-color: var(--affine-border-color);
    }
  `;
__decorate([
    state()
], TOCPanelBody.prototype, "_dragging", void 0);
__decorate([
    state()
], TOCPanelBody.prototype, "_notes", void 0);
__decorate([
    state()
], TOCPanelBody.prototype, "_hiddenNotes", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "page", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "edgeless", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "editorHost", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "mode", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "insertIndex", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "hidePreviewIcon", void 0);
__decorate([
    state()
], TOCPanelBody.prototype, "_selected", void 0);
__decorate([
    query('.panel-list')
], TOCPanelBody.prototype, "panelListElement", void 0);
__decorate([
    query('.toc-panel-body-container')
], TOCPanelBody.prototype, "tocPanelContainer", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "fitPadding", void 0);
__decorate([
    property({ attribute: false })
], TOCPanelBody.prototype, "domHost", void 0);
//# sourceMappingURL=toc-panel-body.js.map